import React from 'react'

import { windowDays } from '@/lib/analytics/periods'
import { fill } from '@/lib/i18n/fill'
import type { Newcomers } from '@/lib/leaderboard'
import { cn } from '@/lib/utils'

import type { Author } from '@/domain'

import { AppContext } from '@/components/base/appcontext'
import LinkWithState from '@/components/base/link'
import UserAvatar from '@/components/base/userAvatar'
import { StatValue } from '@/components/leaderboard/row'
import { Typography } from '@/components/ui/typography'

/** How many faces the one-line row (home) shows. */
const ROW_FACES = 5

// One newcomer's face, opening their profile; the grid also names them.
function Face({ person, named }: { person: Author; named?: boolean }) {
    return (
        <LinkWithState
            to={`/coaches/${person.id}`}
            className={cn(
                'block transition-transform',
                named
                    ? 'flex flex-col items-center gap-1 active:scale-[0.96]'
                    : 'rounded-full ring-2 ring-card active:scale-[0.94]'
            )}
        >
            <UserAvatar
                name={person.login}
                photo={person.photo}
                tintKey={person.id}
                className={named ? 'h-12 w-12 shrink-0' : 'h-8 w-8'}
                fallbackClassName={cn(
                    'font-semibold',
                    named ? 'text-sm' : 'text-xs'
                )}
            />
            {named && (
                <span
                    className="w-full truncate text-center text-[11px] leading-tight text-muted-foreground"
                    dir="auto"
                >
                    {person.login}
                </span>
            )}
        </LinkWithState>
    )
}

// The title says which window the faces joined in when it is not the
// period's: a wider one, or (no window) the newest members ever.
function title(t: (slug: string) => string, newcomers: Newcomers): string {
    if (!newcomers.widened) return t('leaderboard.newcomers.title')
    if (!newcomers.window) return t('leaderboard.newcomers.latest')
    return fill(t('leaderboard.newcomers.recent'), {
        days: windowDays(newcomers.window)
    })
}

/**
 * Who joined in the period, as one line shaped like a board row (home): the
 * newest faces and how many joined in all where a board row has its count.
 */
export function NewcomersRow({ newcomers }: { newcomers: Newcomers }) {
    const appContext = React.useContext(AppContext)
    const { people, total } = newcomers
    if (total === 0) return null

    return (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
            <ul className="m-0 flex shrink-0 list-none -space-x-2 p-0 rtl:space-x-reverse">
                {people.slice(0, ROW_FACES).map((person) => (
                    <li key={person.id}>
                        <Face person={person} />
                    </li>
                ))}
            </ul>
            <Typography variant="title-sm" className="min-w-0 flex-1 truncate">
                {title(appContext.geti18n.bind(appContext), newcomers)}
            </Typography>
            {newcomers.window && <StatValue stat="joined" value={total} />}
        </div>
    )
}

/**
 * Who joined in the period as a grid of named faces (athletes board): the
 * title, how many joined in all, then the newest faces.
 */
export function NewcomersGrid({ newcomers }: { newcomers: Newcomers }) {
    const appContext = React.useContext(AppContext)
    const { people, total } = newcomers
    if (total === 0) return null

    return (
        <section className="mt-3 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
                <Typography variant="title-sm">
                    {title(appContext.geti18n.bind(appContext), newcomers)}
                </Typography>
                {newcomers.window && <StatValue stat="joined" value={total} />}
            </div>
            <ul className="m-0 grid list-none grid-cols-5 gap-x-2 gap-y-3 p-0">
                {people.map((person) => (
                    <li key={person.id} className="min-w-0">
                        <Face person={person} named />
                    </li>
                ))}
            </ul>
        </section>
    )
}
